import { useLazyQuery } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";
import { GET_SURVEYS } from "../graphql/queries/getSurveys";

const DEFAULT_PAGE_SIZE = 10;

const deriveSurveyUrl = (survey) =>
  survey?.partnerConfiguration?.partnerSettings?.url ?? null;

const DEFAULT_FILTERS = {
  search: "",
  searchId: "",
  sourcePartner: "all",
};

export const useSurveyData = (pageSize = DEFAULT_PAGE_SIZE, options = {}) => {
  const { initialFilters } = options;
  const [filters, setFilters] = useState({
    ...DEFAULT_FILTERS,
    ...initialFilters,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: "surveyName",
    direction: "asc",
  });

  const [fetchSurveys_gql, { data, loading, error }] = useLazyQuery(GET_SURVEYS, {
    fetchPolicy: "network-only",
    variables: { filter: {} },
  });

  const fetchSurveys = useCallback(() => {
    fetchSurveys_gql();
  }, [fetchSurveys_gql]);

  const apiErrors = useMemo(() => data?.getSurveyDefinitions?.errors ?? [], [data]);

  const surveys = useMemo(
    () =>
      (data?.getSurveyDefinitions?.items ?? []).map((s) => ({
        ...s,
        surveyDisplayId:
          s?.partnerConfiguration?.partnerSurveyId || s?.surveyId || null,
        surveyUrl: deriveSurveyUrl(s),
      })),
    [data]
  );

  const filteredSurveys = useMemo(() => {
    let result = surveys;

    result = result.filter((s) => s.questions && s.questions.length > 0);

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter((s) =>
        s.surveyName?.toLowerCase().includes(searchLower)
      );
    }

    if (filters.searchId) {
      const idLower = filters.searchId.toLowerCase();
      result = result.filter((s) =>
        s.surveyDisplayId?.toLowerCase().includes(idLower)
      );
    }

    if (filters.sourcePartner && filters.sourcePartner !== "all") {
      result = result.filter(
        (s) => s.sourcePartner.toUpperCase() === filters.sourcePartner
      );
    }

    return result;
  }, [surveys, filters]);

  const sourcePartners = useMemo(() => {
    const partners = filteredSurveys.map((s) => s.sourcePartner).filter(Boolean);
    const uniquePartners = new Map();
    partners.forEach((p) => {
      const key = p.toLowerCase();
      if (!uniquePartners.has(key)) {
        uniquePartners.set(key, p);
      }
    });
    return Array.from(uniquePartners.values())
      .map((p) => p.toUpperCase())
      .sort();
  }, [filteredSurveys]);

  const sortedSurveys = useMemo(() => {
    if (!sortConfig.key) return filteredSurveys;

    return [...filteredSurveys].sort((a, b) => {
      const aValue = a[sortConfig.key] ?? "";
      const bValue = b[sortConfig.key] ?? "";
      const modifier = sortConfig.direction === "asc" ? 1 : -1;

      const result = String(aValue).localeCompare(String(bValue));
      if (result !== 0) return result * modifier;

      return (a.surveyId ?? "").localeCompare(b.surveyId ?? "");
    });
  }, [filteredSurveys, sortConfig]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(sortedSurveys.length / pageSize)),
    [sortedSurveys, pageSize]
  );

  const paginatedSurveys = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedSurveys.slice(start, start + pageSize);
  }, [sortedSurveys, currentPage, pageSize]);

  const updateFilters = useCallback((newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setCurrentPage(1);
  }, []);

  const goToPage = useCallback(
    (page) => {
      setCurrentPage(Math.max(1, Math.min(page, totalPages)));
    },
    [totalPages]
  );

  const resetFilters = useCallback(() => {
    setFilters({ ...DEFAULT_FILTERS, ...initialFilters });
    setCurrentPage(1);
  }, [initialFilters]);

  const resetPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  return {
    surveys,
    filteredSurveys,
    paginatedSurveys,
    loading,
    error,
    apiErrors,
    fetchSurveys,
    filters,
    updateFilters,
    resetFilters,
    resetPage,
    sourcePartners,
    sortConfig,
    setSortConfig,
    pagination: {
      currentPage,
      totalPages,
      totalItems: sortedSurveys.length,
      pageSize,
      goToPage,
    },
  };
};
